import { Dispatch, useCallback, useEffect } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useField } from 'react-final-form';

import { KNM_JUDGMENTS } from 'CorePackage/MainModule/routes/routes';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { JUDGMENTS_BLOCK_ID, getJudgmentBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormJudgment } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgment';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Button } from 'UIPackage/MainModule/components/Button';

import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { DecisionJudgmentItem } from './DecisionJudgmentItem';

interface IDecisionJudgmentProps {
  watchOnly: boolean;
  formRef: IDecisionFormRef | undefined;
  startDate?: string;
}

export const DecisionJudgment = ({ watchOnly, formRef, startDate }: IDecisionJudgmentProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.DISTRICTS));
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_COURTS));
    dispatch(catalogGet(CommonCatalogsNamesEnum.JUDGMENT_COURT_LEVELS));
  }, [dispatch]);

  const addJudgment = useCallback(() => {
    if (!watchOnly && push) {
      const newJudgment: IKnmErknmFormJudgment = {
        guid: undefined,
        dateJudgment: undefined,
        numberJudgment: undefined,
        attachment: undefined,
        kindJudgment: undefined,
        rfRegionCode: undefined,
        court: undefined,
        courtLevelCode: undefined,
        judgmentAction: undefined,
        judgmentSubjectData: undefined,
        canceledJudgments: [],
        createNewJudgment: false,
        listId: uuidv4(),
        isNew: true,
      };

      push('judgments', newJudgment);
    }
  }, [push, watchOnly]);

  const makeRemoveJudgmentHandler = (index: number) => () => !watchOnly && remove && remove('judgments', index);
  const reasons = useField('reasons').input.value;
  const startReasonDate = reasons?.[0]?.prescriptionDate;

  return (
    <Section
      title="Сведения о судебных решениях, принятых по результатам решения"
      type="list"
      id={KNM_JUDGMENTS}
      titleId={JUDGMENTS_BLOCK_ID}
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly}
          onClick={addJudgment}
        />
      }
    >
      <FieldArray name="judgments">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgment, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErknmFormJudgment = fields.value?.[index];
            const id = getJudgmentBlockId(fieldValue.listId);

            return (
              <DecisionJudgmentItem
                formRef={formRef}
                value={fieldValue}
                key={id}
                id={id}
                name={name}
                watchOnly={watchOnly}
                onRemove={makeRemoveJudgmentHandler(index)}
                startDate={startDate || startReasonDate}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};
